import { z } from 'zod/v4';

const PREFIX = 'move:requester-exports:v1:';
const referencesSchema = z.array(z.uuid());
const memory = new Map<string, string[]>();
type ReferenceState = { ids: string[]; persistent: boolean };
const listeners = new Map<string, Set<(state: ReferenceState) => void>>();

function notifyReferences(key: string, state: ReferenceState): void {
  listeners.get(key)?.forEach((listener) => {
    listener(state);
  });
}

/** Same-tab callers subscribe only to their canonical profile/workflow/variant key. */
export function subscribeRequesterExportReferences(
  key: string,
  listener: (state: ReferenceState) => void
): () => void {
  const subscribers = listeners.get(key) ?? new Set<(state: ReferenceState) => void>();
  subscribers.add(listener);
  listeners.set(key, subscribers);
  return () => {
    subscribers.delete(listener);
    if (subscribers.size === 0) listeners.delete(key);
  };
}
let profileEpoch = 0;
let activeProfile: string | null | undefined;

export function clearRequesterExportReferences(): void {
  profileEpoch += 1;
  memory.clear();
  let persistent = true;
  try {
    const keys = Object.keys(sessionStorage).filter((key) => key.startsWith(PREFIX));
    keys.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch {
    // Storage may be disabled; the in-memory fallback is still cleared.
    persistent = false;
  }
  listeners.forEach((_subscribers, key) => {
    notifyReferences(key, { ids: [], persistent });
  });
}

/** Called at the global auth boundary, including while the source page is unmounted. */
export function setRequesterExportProfile(profile: string | null): void {
  if (profile === null || (activeProfile !== undefined && activeProfile !== profile)) {
    clearRequesterExportReferences();
  }
  activeProfile = profile;
  if (profile === null) return;
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(PREFIX) && !key.startsWith(`${PREFIX}${profile}:`))
      .forEach((key) => {
        sessionStorage.removeItem(key);
      });
  } catch {
    // In-memory references remain scoped by the canonical profile.
  }
}

export function requesterExportReferenceKey(
  profile: string,
  workflow: string,
  variant: string
): string {
  return `${PREFIX}${profile}:${encodeURIComponent(workflow)}:${encodeURIComponent(variant)}`;
}

export function readRequesterExportReferences(key: string): ReferenceState {
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw === null ? [] : referencesSchema.parse(JSON.parse(raw));
    const ids = memory.get(key) ?? parsed;
    // Reading can succeed in a read-only storage environment. Probe writes before promising recovery.
    sessionStorage.setItem(key, JSON.stringify(ids));
    memory.set(key, ids);
    return { ids, persistent: true };
  } catch {
    return { ids: memory.get(key) ?? [], persistent: false };
  }
}

/** Persists opaque IDs only; never filters, acceptance keys, file URLs or financial metadata. */
export function writeRequesterExportReferences(key: string, ids: string[]): boolean {
  const valid = referencesSchema.parse(ids);
  memory.set(key, valid);
  let persistent = true;
  try {
    sessionStorage.setItem(key, JSON.stringify(valid));
  } catch {
    persistent = false;
  }
  notifyReferences(key, { ids: valid, persistent });
  return persistent;
}

/** Hides one caller reference without cancelling or deleting the server-owned export. */
export function removeRequesterExportReference(key: string, id: string): boolean {
  const { ids } = readRequesterExportReferences(key);
  return writeRequesterExportReferences(
    key,
    ids.filter((reference) => reference !== id)
  );
}

export function isRequesterExportProfile(profile: string): boolean {
  return activeProfile === profile;
}

export function requesterExportProfileEpoch(): number {
  return profileEpoch;
}
